<template>
  <div
    class="tw-flex tw-max-w-full tw-gap-2 tw-group"
    :class="containerClass"
    data-testid="adjustments-account-filled"
  >
    <AdjustmentsAccountItem
      :label="originalAccountIdentifier"
      :name="accountIdentifier"
      :grow="true"
    ></AdjustmentsAccountItem>
    <div v-if="!isShowMode" class="tw-flex tw-items-center tw-gap-2">
      <button
        type="button"
        class="tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer"
        :class="iconClasses"
        data-testid="adjustments-account-edit"
        @click="editSelectedAccount"
        @focus="editSelectedAccount"
      >
        <i class="fa fa-pencil tw-min-w-0"></i>
      </button>

      <a
        class="
          tw-opacity-0
          group-hover:tw-opacity-100
          tw-text-sf-gray-darker
          hover:tw-text-sf-gray-dark
        "
        data-testid="adjustments-account-link"
        data-heap="adjustments-account-link"
        target="_blank"
        :href="accountEntryPath"
        tabindex="-1"
        @click.stop
      >
        <i class="fa fa-external-link"></i>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  SubAdjustmentRowAccount,
  SubAdjustmentRowAccountNew,
} from '@app/application/adjustments/types'

import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import AdjustmentsAccountItem from './adjustments-account-item.vue'
import { useAdjustmentsModalStore } from '@app/application/adjustments/store/adjustment-modal'

interface Props {
  account: SubAdjustmentRowAccount
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  account: () => ({} as SubAdjustmentRowAccountNew),
})

const modalStore = useAdjustmentsModalStore()
const { mode } = storeToRefs(modalStore)
const isShowMode = computed(() => mode.value === 'show')

const emit =
  defineEmits<{
    'update:modelValue': [value: SubAdjustmentRowAccount]
    'edit-selected-account': [value: boolean]
  }>()

const containerClass = computed(() => {
  return {
    'tw-max-h-[30px]':
      accountIdentifier.value && !originalAccountIdentifier.value,
    'tw-py-1.5': accountIdentifier.value && !originalAccountIdentifier.value,
  }
})

const iconClasses = computed(() => {
  return {
    'tw-text-sf-gray-darker': !props.disabled,
    'tw-text-sf-gray-lighter': props.disabled,
    '!tw-cursor-not-allowed': props.disabled,
  }
})

const accountIdentifier = computed(() => {
  if (!props.account.number || !props.account.name) {
    return ''
  }

  return `${props.account.number} ${props.account.name}`
})

const originalAccountIdentifier = computed(() => {
  if (!props.account.originalNumber && !props.account.originalName) {
    return ''
  }

  return `${props.account.originalNumber ?? props.account.number} ${
    props.account.originalName ?? props.account.name
  }`
})

const accountEntryPath = computed(() => {
  return window.Silverfin.Routes.ledger_account_entry_path({
    firm_id: window.Silverfin.currentFirmId,
    company_id: window.Silverfin.currentCompanyId,
    ledger_id: window.Silverfin.currentLedgerId(),
    current_ledger_id: window.Silverfin.currentLedgerId(),
    account_number: props.account.number,
  })
})

const editSelectedAccount = (): void => {
  emit('edit-selected-account', true)
}
</script>
